// Номер стадии (league_stages.priority) и этап: группа или плей-офф.
// Файл одинаковый на сайте (ingame-site-admin/src/utilities/stagePriority.js)
// и в приложении (ingame-admin/src/utilities/stagePriority.js) — править оба.
//
// Сервер относит стадию к групповому этапу, если
// priority <= league_settings.group_stages_amount («Туров, идущих в группу»),
// остальные — плей-офф. Поэтому групповой тур получает следующий номер,
// но не больше «Туров в группе»: если туров больше, чем указано в настройках,
// лишние делят последний номер группы и всё равно остаются в группе.
// Плей-офф всегда идёт после последней стадии и после всех туров группы.

// format: { leagueType, groupStagesAmount } из настроек сезона, либо null, пока не загружены
export const isGroupLeague = (format) => Boolean(format) && format.leagueType === 2;

export const canHaveGroupStages = (format) => isGroupLeague(format) && format.groupStagesAmount > 0;

export const isGroupStage = (priority, format) => (
    canHaveGroupStages(format)
    && Number(priority) > 0
    && Number(priority) <= format.groupStagesAmount
);

export const maxStagePriority = (stages) => (stages || []).reduce(
    (max, stage) => Math.max(max, Number(stage.priority) || 0), 0
);

export const nextStagePriority = (stages) => maxStagePriority(stages) + 1;

// 'group' | 'playoff' — этап существующей стадии
export const stagePhaseOf = (stage, format) => (isGroupStage(stage.priority, format) ? 'group' : 'playoff');

// Этап новой стадии по умолчанию: пока в сезоне нет плей-офф — групповой
export const defaultStagePhase = (stages, format) => {
    if (!canHaveGroupStages(format)) return 'playoff';
    const hasPlayoff = (stages || []).some(stage => !isGroupStage(stage.priority, format));
    return hasPlayoff ? 'playoff' : 'group';
};

// priority для amount новых стадий выбранного этапа
export const prioritiesForNewStages = (stages, format, phase, amount = 1) => {
    const max = maxStagePriority(stages);
    if (!isGroupLeague(format)) {
        return Array.from({ length: amount }, (_, i) => max + 1 + i);
    }
    const groupStages = format.groupStagesAmount > 0 ? format.groupStagesAmount : 0;
    if (phase === 'group' && groupStages > 0) {
        const maxGroup = (stages || [])
            .filter(stage => isGroupStage(stage.priority, format))
            .reduce((acc, stage) => Math.max(acc, Number(stage.priority) || 0), 0);
        return Array.from({ length: amount }, (_, i) => Math.min(maxGroup + 1 + i, groupStages));
    }
    const start = Math.max(max, groupStages) + 1;
    return Array.from({ length: amount }, (_, i) => start + i);
};

// Номера в названиях новых туров («4 Тур»): по счёту внутри этапа, а не по priority —
// у групповых туров сверх «Туров в группе» priority одинаковый
export const nameNumbersForNewStages = (stages, format, phase, amount = 1) => {
    if (!isGroupLeague(format)) return prioritiesForNewStages(stages, format, phase, amount);
    const inPhase = (stages || []).filter(stage => stagePhaseOf(stage, format) === phase).length;
    return Array.from({ length: amount }, (_, i) => inPhase + 1 + i);
};

// Новый priority, если перенести существующую стадию в другой этап
export const priorityForPhaseChange = (stages, format, stage, phase) => prioritiesForNewStages(
    (stages || []).filter(other => String(other.id) !== String(stage.id)), format, phase, 1
)[0];

// priority тура tourNumber в расписании из генератора: в групповой лиге — не дальше «Туров в группе»
export const schedulePriority = (tourNumber, format) => (
    canHaveGroupStages(format) ? Math.min(tourNumber, format.groupStagesAmount) : tourNumber
);

// Подсказка для админа: куда попадёт стадия
export const stagePhaseHint = (priority, format) => {
    if (!isGroupLeague(format)) return '';
    if (!(format.groupStagesAmount > 0)) {
        return 'В настройках сезона не указано, сколько туров идёт в группу — все стадии попадут в плей-офф.';
    }
    return isGroupStage(priority, format) ? 'Групповой этап.' : 'Плей-офф.';
};

export const NO_GROUP_STAGES_TEXT = 'В настройках сезона не указано «Туров, идущих в группу» — '
    + 'групповые туры не создать, они уйдут в плей-офф. Укажите число туров группового этапа в карточке лиги.';
